import logoDark from "@public/logo.svg";
import logoLight from "@public/logo_on_light.svg";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { NotificationBell } from "@/components/notifications";
import { ThemeToggle } from "@/components/theme-toggle";
import { Link } from "@/components/ui/link";
import { UserMenu } from "@/components/user-menu";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./navigation.i18n";

// User-specific settings pages that should NOT highlight the "Settings" nav item
const USER_SETTINGS_PAGES = ["/settings/profile", "/settings/security", "/settings/notifications"];

type NavigationProps = {
  user: {
    name?: string | null;
    email?: string | null;
    image?: string | null;
    tenantSlug: string;
    permissions: string[];
  };
  logoLightUrl?: string | null;
  logoDarkUrl?: string | null;
  isPartner?: boolean;
};

export function Navigation({
  user,
  logoLightUrl,
  logoDarkUrl,
  isPartner = false,
}: NavigationProps) {
  const { pathname } = useLocation();
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const t = useT(T);

  // Avoid hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  // Determine logo based on theme mode
  // Use custom tenant logo if available, otherwise fall back to default
  const isDark = mounted && resolvedTheme === "dark";
  const customLogo = isDark ? logoDarkUrl : logoLightUrl;
  const defaultLogo = isDark ? logoDark : logoLight;
  const logoSrc = customLogo || defaultLogo;
  const useCustomLogo = !!customLogo;

  // Base links available to all tenants
  const baseLinks = [
    { href: "/", label: t.dashboard },
    { href: "/clients", label: t.clients },
  ];

  // Compliance link - only for users with compliance permission
  const hasComplianceAccess = (user.permissions || []).includes("compliance");
  const complianceLinks = hasComplianceAccess ? [{ href: "/compliance", label: t.compliance }] : [];

  // Settings link - only show if user has permissions beyond "advisor"
  // Check if user has developer, admin, or designer permissions
  const hasSettingsAccess = (user.permissions || []).some(
    (p) => p === "developer" || p === "admin" || p === "designer",
  );
  const settingsLinks = hasSettingsAccess ? [{ href: "/settings", label: t.settings }] : [];

  // Admin link only for admin users of the quantfolio tenant or a partner tenant —
  // mirrors the API gate (createRequireQuantfolioOrPartner).
  const isQuantfolio = user.tenantSlug === "quantfolio";
  const isAdmin = (user.permissions || []).includes("admin");
  const adminLinks =
    (isQuantfolio || isPartner) && isAdmin ? [{ href: "/admin", label: t.admin }] : [];

  const links = [...baseLinks, ...complianceLinks, ...adminLinks, ...settingsLinks];

  return (
    <nav className="border-b bg-card">
      <div className="px-4 max-w-7xl mx-auto">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center space-x-8">
            <Link to="/" className="flex items-center">
              {useCustomLogo ? (
                // Custom tenant logo - use regular img since it's from external storage
                // biome-ignore lint/a11y/useAltText: logo image
                <img src={logoSrc} className="h-10 max-w-[150px] object-contain" />
              ) : (
                <img src={logoSrc} alt="Quantfolio" width={150} height={40} />
              )}
            </Link>
            <div className="flex space-x-6">
              {links.map((link) => {
                // Check if current page is active
                // For exact match or when pathname starts with link.href (for sub-pages)
                // Exclude user-specific pages (profile, security, notifications) from Settings
                const isActive =
                  pathname === link.href ||
                  (link.href !== "/" &&
                    pathname.startsWith(`${link.href}/`) &&
                    !USER_SETTINGS_PAGES.some((p) => pathname.startsWith(p)));

                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={cn(
                      "relative text-sm font-medium transition-colors hover:text-primary py-1",
                      isActive
                        ? "text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:rounded-full after:bg-[hsl(var(--brand))]"
                        : "text-muted-foreground",
                    )}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <ThemeToggle />
            <NotificationBell />
            <UserMenu user={user} />
          </div>
        </div>
      </div>
    </nav>
  );
}
